import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core';
import { useState } from 'react';
import { useEditorStore } from '../store';
import { getToolById } from './tools/registry';
import type { DesignElement } from '../types/editor.types';
import { generateId } from '../utils';
import { ElementRenderer } from './canvas/ElementRenderer';

interface Props { children: React.ReactNode }

export function DndEngine({ children }: Props) {
  const { addElement, moveElement, addRow } = useEditorStore();
  const [activeItem, setActiveItem] = useState<{ type: string; data: unknown } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveItem({ type: String(active.data.current?.type), data: active.data.current });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveItem(null);
    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Tool dropped onto a column drop zone
    if (activeData?.type === 'tool' && overData?.type === 'column') {
      const tool = getToolById(activeData.toolId as string);
      if (!tool) return;
      const element: DesignElement = {
        id: generateId(),
        type: tool.id,
        content: { ...tool.defaultContent },
        styles: { ...tool.defaultStyles },
      };
      addElement(overData.rowId as string, overData.columnId as string, element);
      return;
    }

    // Tool dropped onto canvas / empty area — create row then add element
    if (activeData?.type === 'tool' && (!overData || overData.type === 'canvas')) {
      const tool = getToolById(activeData.toolId as string);
      if (!tool) return;
      addRow();
      const rows = useEditorStore.getState().design.body.rows;
      const lastRow = rows[rows.length - 1];
      if (!lastRow) return;
      const element: DesignElement = {
        id: generateId(),
        type: tool.id,
        content: { ...tool.defaultContent },
        styles: { ...tool.defaultStyles },
      };
      addElement(lastRow.id, lastRow.columns[0].id, element);
      return;
    }

    // Element reordering across or within columns
    if (activeData?.type === 'element' && overData?.type === 'element') {
      const fromRowId = activeData.rowId as string;
      const fromColId = activeData.columnId as string;
      const toRowId = overData.rowId as string;
      const toColId = overData.columnId as string;

      const { design } = useEditorStore.getState();
      const fromRow = design.body.rows.find((r) => r.id === fromRowId);
      const toRow = design.body.rows.find((r) => r.id === toRowId);
      const fromCol = fromRow?.columns.find((c) => c.id === fromColId);
      const toCol = toRow?.columns.find((c) => c.id === toColId);
      if (!fromCol || !toCol) return;

      const fromIdx = fromCol.elements.findIndex((e) => e.id === active.id);
      const toIdx = toCol.elements.findIndex((e) => e.id === over.id);
      if (fromIdx !== -1 && toIdx !== -1) {
        moveElement(fromRowId, fromColId, fromIdx, toRowId, toColId, toIdx);
      }
    }
  };

  const overlayElement = activeItem?.type === 'element'
    ? (activeItem.data as { element: DesignElement }).element
    : null;

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      {children}
      <DragOverlay dropAnimation={{ duration: 150, easing: 'ease' }}>
        {overlayElement && (
          <div className="pointer-events-none opacity-90 shadow-elevated rounded-lg bg-surface dark:bg-dark-800 p-2">
            <ElementRenderer element={overlayElement} />
          </div>
        )}
        {activeItem?.type === 'tool' && (
          <div className="pointer-events-none bg-accent-muted dark:bg-accent/20
            text-accent dark:text-accent-light
            border border-accent/30 rounded-lg px-3 py-2 text-xs font-medium shadow-elevated">
            {(activeItem.data as { tool: { label: string } }).tool?.label}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
